import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Paper,
  Chip,
  Button,
  Typography,
  Link,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ReplyIcon from "@mui/icons-material/Reply";
import { styled } from "@mui/material/styles";
import { useNotificationContext } from "../contextProviders/NotificationProvider";
import MessageReply from "./MessageReply";
import { useConversationsApi } from "../hooks/useConversationsApi";
import { messagesApi } from "../API/messagesApi";
import { formatDateTime } from "../utils/formatDateTime";
// ...existing code...
import styles from "../pages/Dashboard/Dashboard.module.css";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import Badge from "@mui/material/Badge";

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontSize: "0.75rem",
  height: 24,
  minWidth: 84,
  ...(status === "Unread" && {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  }),
  ...(status === "InProgress" && {
    backgroundColor: "#fff3e0",
    color: "#f57c00",
  }),
  // ...(status === "Read" && {
  //   backgroundColor: "#e8f5e9",
  //   color: "#4caf50",
  // }),
}));

// Helper function to extract first name from full name
// Handles: Regular users (firstName only) and SSO users (full name in firstName field)
const getFirstName = (name) => {
  if (!name) return "Unknown";
  return name.split(" ")[0] || "Unknown";
};

const MessageList = () => {
  // Helper for status label
  const getStatusLabel = (status) => {
    switch (status) {
      case "Unread":
        return "Unread";
      case "InProgress":
        return "In-Progress";
      default:
        return status;
    }
  };
  const { loading, lockConversation, fetchOperatorConversations } =
    useConversationsApi();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConsumerCredit, setSelectedConsumerCredit] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [sentCount, setSentCount] = useState(null);
  const [sentDays, setSentDays] = useState(7);
  const { showNotification } = useNotificationContext();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // Use SignalR context for real-time features
  const { useSignalRContext } = require("../contextProviders/SignalRProvider");
  const signalR = useSignalRContext();

  // Track processed messages to prevent duplicates
  const processedMessagesRef = useRef(new Set());
  // Track if event listener is already registered
  const eventListenerRegisteredRef = useRef(false);
  // Keep conversations in ref for event handler access
  const conversationsRef = useRef(conversations);
  
  // Update conversations ref when conversations change
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Fetch conversations on mount
  const fetchData = useCallback(async () => {
    if (!user?.id) {
      setConversations([]);
      return;
    }
    const result = await fetchOperatorConversations(user.id);
    // If API returns { success, data, ... }, use result.data
    if (result && !result.error) {
      if (Array.isArray(result.data)) {
        setConversations(result.data);
      } else if (Array.isArray(result)) {
        setConversations(result);
      } else {
        setConversations([]);
      }
    } else {
      setConversations([]);
    }
  }, [user?.id, fetchOperatorConversations]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch operator's sent message count
  const fetchSentCount = useCallback(async () => {
    const result = await messagesApi.getOperatorSentCount();
    if (result && !result.error) {
      // Standard ApiResponse: { success, data: { count, days } }
      const data = result.data || result;
      setSentCount(data.count ?? 0);
      setSentDays(data.days || 7);
    }
  }, []);

  // Fetch sent count on mount
  useEffect(() => {
    fetchSentCount();
  }, [fetchSentCount]);

  // Periodic refresh as fallback (every 30 seconds) to ensure queue stays in sync
  // This helps recover from missed SignalR events
  useEffect(() => {
    if (!user?.id) return;

    const intervalId = setInterval(() => {
      console.log("[MessageList] 🔄 Periodic refresh (fallback mechanism)");
      fetchData();
      fetchSentCount();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [user?.id, fetchData, fetchSentCount]);

  // Filter conversations to only 'Unread' status, then by search term
  useEffect(() => {
    let filtered = conversations.filter((conv) => conv.status === "Unread");
    if (searchTerm) {
      filtered = filtered.filter(
        (conv) =>
          (conv.consumerName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (conv.clientName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (conv.lastMessage?.body || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }
    setFilteredConversations(filtered);
  }, [conversations, searchTerm]);

  // Listen to SignalRProvider's processed events instead of direct SignalR connection
  useEffect(() => {
    console.log(
      "[MessageList] Setting up custom event listener for processed SignalR events"
    );

    const handleNewInboundMessage = (event) => {
      const message = event.detail;
      console.log("[MessageList] Received processed SignalR event:", message);

      // Check if this is an inbound message
      if (message.conversationId || message.ConversationId) {
        const conversationId = message.conversationId || message.ConversationId;
        console.log(
          "[MessageList] Processing inbound message for conversation:",
          conversationId
        );

        // Check if conversation is currently locked (being viewed)
        const conversation = conversationsRef.current.find(c => c.id === conversationId);
        const isCurrentlyLocked = conversation?.isLocked && conversation?.lockedByOperatorId;
        
        console.log("[MessageList] Conversation lock status:", {
          conversationId,
          isLocked: conversation?.isLocked,
          lockedBy: conversation?.lockedByOperatorId,
          shouldSkipQueueUpdate: isCurrentlyLocked
        });

        // Only refresh queue and increment count if conversation is NOT currently locked
        // If locked, the operator viewing it will handle the message in ChatInterface
        if (!isCurrentlyLocked) {
          console.log("[MessageList] Conversation not locked, adding to queue");
          
          // REMOVED: Snackbar notification - too intrusive, message will appear in conversation list
          // showNotification(
          //   `New message from ${message.consumerName || "Consumer"}: ${
          //     message.body?.substring(0, 40) || "..."
          //   }`,
          //   "info"
          // );
          
          // Refresh conversation list
          fetchData();
          
          // Directly increment the count
          setUnreadCounts((prev) => {
            const currentCount = prev[conversationId] || 0;
            const newCount = currentCount + 1;
            console.log(
              "[MessageList] Count update:",
              conversationId,
              currentCount,
              "->",
              newCount
            );
            return {
              ...prev,
              [conversationId]: newCount,
            };
          });
        } else {
          console.log("[MessageList] Conversation is locked, skipping queue update - message will appear in active ChatInterface");
        }
      }
    };

    // Listen to the custom event dispatched by SignalRProvider (after deduplication)
    window.addEventListener(
      "signalr-new-message-notification",
      handleNewInboundMessage
    );
    console.log("[MessageList] Custom event listener registered");

    // Listen for conversation locked events to refresh message list instantly
    const handleConversationLocked = (event) => {
      console.log("[MessageList] 🔒 Conversation locked, refreshing list:", event.detail);
      // Immediately refresh conversation list when anyone locks a conversation
      fetchData();
    };

    // Listen for conversation unlocked events to add conversation instantly to queue
    const handleConversationUnlocked = (event) => {
      console.log("[MessageList] 🔓 Conversation unlocked:", event.detail);
      const unlockData = event.detail;
      const conversationId = unlockData.ConversationId || unlockData.conversationId;
      
      // OPTIMIZATION: Only process if status is Unread (should appear in queue)
      if (unlockData.Status === 'Unread' || unlockData.status === 'Unread') {
        console.log("[MessageList] ✅ Status is Unread - adding to queue instantly");
        
        // Check if conversation already exists in state
        setConversations(prev => {
          const existingConv = prev.find(c => c.id === conversationId);
          
          if (existingConv) {
            // Update existing conversation status
            console.log("[MessageList] 🔄 Updating existing conversation status to Unread");
            return prev.map(c => 
              c.id === conversationId
                ? { ...c, status: 'Unread', isLocked: false, lockedByOperatorId: null }
                : c
            );
          } else {
            // ✅ NEW: Try to add conversation directly from SignalR data if available
            if (unlockData.ConsumerName || unlockData.consumerName) {
              console.log("[MessageList] 📥 Adding conversation directly from SignalR data");
              const newConversation = {
                id: conversationId,
                consumerId: unlockData.ConsumerId || unlockData.consumerId,
                consumerName: unlockData.ConsumerName || unlockData.consumerName,
                clientId: unlockData.ClientId || unlockData.clientId,
                clientName: unlockData.ClientName || unlockData.clientName,
                status: 'Unread',
                isLocked: false,
                lockedByOperatorId: null,
                lastMessageAt: unlockData.LastMessageAt || unlockData.lastMessageAt || new Date().toISOString(),
                deliveryMethod: 'Webchat'
              };
              return [...prev, newConversation];
            } else {
              // Fallback: fetch to get full details
              console.log("[MessageList] 📥 No full data in event, fetching from API");
              fetchData();
              return prev;
            }
          }
        });
      } else {
        console.log("[MessageList] ⏭️ Status is not Unread, skipping queue update");
        // Still fetch to ensure UI consistency
        fetchData();
      }
    };

    window.addEventListener("signalr-conversation-locked", handleConversationLocked);
    window.addEventListener("signalr-conversation-unlocked", handleConversationUnlocked);

    // Handle SignalR reconnection - refresh data to sync any missed updates
    const handleReconnection = () => {
      console.log("[MessageList] 🔄 SignalR reconnected - refreshing conversation list");
      fetchData();
      // Reset unread counts since we can't reliably track during disconnect
      // The conversation list fetch will show current state
    };
    window.addEventListener("signalr-reconnected", handleReconnection);

    // Cleanup function
    return () => {
      console.log("[MessageList] Cleaning up custom event listeners");
      window.removeEventListener(
        "signalr-new-message-notification",
        handleNewInboundMessage
      );
      window.removeEventListener("signalr-conversation-locked", handleConversationLocked);
      window.removeEventListener("signalr-conversation-unlocked", handleConversationUnlocked);
      window.removeEventListener("signalr-reconnected", handleReconnection);
    };
  }, []); // No dependencies needed since we're using window events

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const columns = [
    {
      field: "clientName",
      headerName: "Client",
      width: 200,
      renderCell: (params) => {
        const unread = unreadCounts[params.row.id] || 0;
        return (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{params.value}</span>
            <Badge
              badgeContent={unread > 9 ? "9+" : unread}
              color="error"
              invisible={unread === 0}
              sx={{
                marginLeft: 1,
                "& .MuiBadge-badge": {
                  fontSize: "0.75rem",
                  top: "0px",
                  right: "18px",
                },
              }}
            />
          </span>
        );
      },
    },
    {
      field: "consumerName",
      headerName: "Consumer",
      width: 200,
      renderCell: (params) => getFirstName(params.value),
    },
    {
      field: "deliveryMethod",
      headerName: "Method",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value ?? "Webchat"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <StatusChip
          label={getStatusLabel(params.value)}
          size="small"
          status={params.value}
        />
      ),
    },
    {
      field: "unreadSince",
      headerName: "Date",
      width: 170,
      renderCell: (params) => {
        if (!params.value) return "-";
        // Use formatDateTime utility for proper UTC to local timezone conversion
        // Returns dd/MM/yyyy HH:mm:ss format in user's local timezone
        return formatDateTime(params.value);
      },
    },
    {
      field: "actions",
      headerName: "Reply",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.row.status === "Unread" ? (
          <IconButton
            size="small"
            variant="outlined"
            onClick={() => handleReply(params.row)}
          >
            <ReplyIcon
              sx={{
                color: "var(--color-icon)",
                "&:hover": { color: "var(--color-icon-hover)" },
              }}
            />
          </IconButton>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
  ];

  // Ensure each row has a unique ID
  const rows = filteredConversations.map((conversation) => ({
    id: conversation.id,
    ...conversation,
  }));

  const handleBackToList = () => {
    setSelectedConversation(null);
    fetchData();
    fetchSentCount(); // Refresh sent count when returning from conversation
  };

  const resetUnreadCount = (conversationId) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [conversationId]: 0,
    }));
  };

  const handleReply = async (conversation) => {
    // First, check frontend status (quick check)
    if (
      conversation.status === "InProgress" ||
      conversation.status === "Assigned"
    ) {
      showNotification(
        "This conversation is already being handled by another operator",
        "warning"
      );
      return;
    }

    // ⚡ OPTIMISTIC UI - Remove from queue INSTANTLY for better UX
    setConversations(prev => prev.filter(c => c.id !== conversation.id));
    resetUnreadCount(conversation.id);

    // Now verify lock on backend
    console.log("[MessageList] Attempting to lock conversation:", conversation.id);
    const lockResult = await lockConversation(conversation.id, {
      operatorId: user.id,
      status: "InProgress",
    });

    console.log("[MessageList] Lock result:", lockResult);

    // Check if lock failed (returns null or has error property)
    if (!lockResult || lockResult.error) {
      console.log("[MessageList] Lock failed, showing notification");
      // Lock failed - conversation is locked by another operator
      showNotification(
        "This conversation is locked by another operator. Please try another conversation.",
        "warning"
      );
      // Restore conversation to queue
      fetchData();
      return;
    }

    console.log("[MessageList] Lock successful, opening chat");
    // ✅ Lock successful! Continue with opening chat
    // Dispatch custom event for local window listeners
    const event = new CustomEvent("signalr-conversation-locked", { 
      detail: { conversationId: conversation.id, lockedBy: user } 
    });
    window.dispatchEvent(event);

    // Set lock expiry time (5 minutes from now)
    const lockExpiry = Date.now() + 5 * 60 * 1000; // 5 minutes in milliseconds
    const conversationWithLock = {
      ...conversation,
      lockExpiry,
      lockedBy: user.id,
    };

    // Only set selected conversation; MessageReply will fetch balance
    setSelectedConversation(conversationWithLock);
  };

  // Call this when a new inbound message arrives for a conversation
  const incrementUnreadCount = (conversationId) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || 0) + 1,
    }));
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {selectedConversation ? (
        <MessageReply
          conversation={selectedConversation}
          consumerCredit={selectedConsumerCredit}
          onBack={handleBackToList}
          unreadConversationCount={filteredConversations.length}
          existingUnreadConversationIds={filteredConversations.map(c => c.id)}
        />
      ) : (
        <>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 30,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <h1 className={styles.dashboardTitle}>MESSAGES</h1>
              {sentCount !== null && (
                <Chip
                  icon={<SendIcon style={{ fontSize: 16 }} />}
                  label={`Sent: ${sentCount} (last ${sentDays} days)`}
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 500, fontSize: "0.82rem" }}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 32,
                alignItems: "center",
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  position: "relative",
                  minWidth: 0,
                  flex: "1 1 120px",
                  width: "100%",
                }}
              >
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    padding: "8px 24px 8px 40px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    fontSize: "0.98rem",
                    width: "100%",
                    height: 40,
                    boxSizing: "border-box",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "55%",
                    transform: "translateY(-50%)",
                    color: "#888",
                    pointerEvents: "none",
                  }}
                >
                  <SearchIcon />
                </span>
              </div>
            </div>
          </div>

          <Paper
            sx={{ height: 580, width: "100%", borderRadius: 2, boxShadow: 2 }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={36}
              loading={loading}
              pageSizeOptions={[10, 15, 30, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              disableRowSelectionOnClick
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "14px",
                  fontWeight: "bold",
                },
              }}
              localeText={{
                noRowsLabel: "No messages found",
                loadingOverlayLabel: "Loading...",
              }}
            />
          </Paper>
        </>
      )}
    </div>
  );
};

export default MessageList;
